import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, X, Users2, Save, BookmarkPlus, Trash2, Loader2 } from "lucide-react";

const LIST_OPS = ["-in", "-notIn"];
const BOOL_PROPS = ["user.accountEnabled"];

// ---- Entra membership rule (user.* properties, single-quoted -> double-quoted values) ----
export function buildRule(clauses, join) {
  const j = join === "or" ? "or" : "and";
  const parts = [];
  for (const c of clauses || []) {
    const prop = (c.property || "").trim();
    const op = (c.operator || "").trim();
    const val = c.value;
    if (!prop || !op) continue;
    let rendered;
    if (LIST_OPS.includes(op)) {
      const items = String(val || "").split(",").map((s) => s.trim()).filter(Boolean);
      rendered = "[" + items.map((i) => `"${i}"`).join(", ") + "]";
    } else if (String(val).toLowerCase() === "true" || String(val).toLowerCase() === "false") {
      rendered = String(val).toLowerCase();
    } else {
      rendered = `"${val}"`;
    }
    parts.push(`(${prop} ${op} ${rendered})`);
  }
  return parts.join(` ${j} `);
}

export function parseRule(rule) {
  if (!rule || !rule.trim()) return { clauses: [], join: "and" };
  const join = /\)\s+or\s+\(/.test(rule) ? "or" : "and";
  const re = /\(([^\s()]+)\s+(-\w+)\s+([^()]+?)\)/g;
  const clauses = [];
  let m;
  while ((m = re.exec(rule))) {
    let val = m[3].trim();
    if (val.startsWith("[") && val.endsWith("]")) {
      val = val.slice(1, -1).split(",").map((s) => s.trim().replace(/^"|"$/g, "")).join(", ");
    } else {
      val = val.replace(/^"|"$/g, "");
    }
    clauses.push({ property: m[1], operator: m[2], value: val });
  }
  return { clauses, join };
}

// ---- Exchange Online recipient filter (PascalCase properties, single-quoted values, -and/-or) ----
export function buildFilterExo(clauses, join) {
  const j = join === "-or" ? "-or" : "-and";
  const parts = [];
  for (const c of clauses || []) {
    const prop = (c.property || "").trim();
    const op = (c.operator || "").trim();
    if (!prop || !op) continue;
    const rendered = String(c.value ?? "").replace(/'/g, "''");
    parts.push(`(${prop} ${op} '${rendered}')`);
  }
  return parts.join(` ${j} `);
}

export function parseFilterExo(rule) {
  if (!rule || !rule.trim()) return { clauses: [], join: "-and" };
  const join = /\)\s+-or\s+\(/.test(rule) ? "-or" : "-and";
  const re = /\(([A-Za-z0-9]+)\s+(-\w+)\s+'([^']*)'\)/g;
  const clauses = [];
  let m;
  while ((m = re.exec(rule))) clauses.push({ property: m[1], operator: m[2], value: m[3] });
  return { clauses, join };
}

const validOnly = (clauses) => (clauses || []).filter((c) => c.property && c.operator && String(c.value ?? "").trim() !== "");

export function RuleBuilder({ value, initialClauses, initialJoin, onChange, testid = "rule-builder",
  variant = "entra", preview = false, templates = false }) {
  const { t } = useLang();
  const isExo = variant === "exo";
  const cfg = isExo
    ? { schemaUrl: "/dynamic-distribution-lists/filter-schema", build: buildFilterExo, parse: parseFilterExo, kind: "exo", defaultJoin: "-and" }
    : { schemaUrl: "/groups/rule-schema", build: buildRule, parse: parseRule, kind: "entra", defaultJoin: "and" };

  const [schema, setSchema] = useState({ properties: [], operators: [], joins: [] });
  const init = useMemo(() => {
    if (initialClauses && initialClauses.length) return { clauses: initialClauses, join: initialJoin || cfg.defaultJoin };
    return cfg.parse(value || "");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [clauses, setClauses] = useState(init.clauses.length ? init.clauses : [{ property: isExo ? "Department" : "user.department", operator: "-eq", value: "" }]);
  const [join, setJoin] = useState(init.join);
  const [tpls, setTpls] = useState([]);
  const [savingTpl, setSavingTpl] = useState(false);
  const [tplName, setTplName] = useState("");
  const [showSave, setShowSave] = useState(false);
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => { api.get(cfg.schemaUrl).then((r) => setSchema(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (templates) loadTpls(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadTpls = () => api.get("/groups/rule-templates").then((r) => setTpls((r.data || []).filter((x) => x.kind === cfg.kind))).catch(() => {});

  const emit = (cl, j) => onChange?.(cfg.build(cl, j), { clauses: validOnly(cl), join: j });
  const setBoth = (cl, j) => { setClauses(cl); setJoin(j); emit(cl, j); };
  const update = (i, key, v) => setBoth(clauses.map((c, idx) => (idx === i ? { ...c, [key]: v } : c)), join);
  const add = () => setBoth([...clauses, { property: schema.properties[0] || (isExo ? "Department" : "user.department"), operator: "-eq", value: "" }], join);
  const remove = (i) => setBoth(clauses.filter((_, idx) => idx !== i), join);

  const previewText = useMemo(() => cfg.build(clauses, join), [clauses, join]); // eslint-disable-line react-hooks/exhaustive-deps
  const joinOpts = (schema.joins && schema.joins.length ? schema.joins : [cfg.defaultJoin, isExo ? "-or" : "or"]);

  // live estimated member count
  useEffect(() => {
    if (!preview) return;
    const vc = validOnly(clauses);
    if (!vc.length) { setCount(null); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setCounting(true);
    debounceRef.current = setTimeout(() => {
      api.post("/groups/rule-preview", { kind: cfg.kind, join, clauses: vc })
        .then((r) => setCount(r.data.matched)).catch(() => setCount(null)).finally(() => setCounting(false));
    }, 500);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [JSON.stringify(clauses), join, preview]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyTpl = (tid) => {
    const tp = tpls.find((x) => x.id === tid);
    if (tp) setBoth(tp.clauses, tp.join || cfg.defaultJoin);
  };
  const saveTpl = async () => {
    if (!tplName.trim()) return;
    setSavingTpl(true);
    try {
      await api.post("/groups/rule-templates", { name: tplName.trim(), kind: cfg.kind, join, clauses: validOnly(clauses) });
      toast.success(t("rule.templateSaved")); setTplName(""); setShowSave(false); loadTpls();
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingTpl(false); }
  };
  const delTpl = async (tid, e) => {
    e.stopPropagation();
    try { await api.delete(`/groups/rule-templates/${tid}`); loadTpls(); } catch (err) { toast.error(apiErr(err)); }
  };

  const joinLabel = (v) => ((v === "or" || v === "-or") ? t("rule.joinOr") : t("rule.joinAnd"));

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="text-xs font-semibold text-zinc-600">{isExo ? t("rule.exoTitle") : t("rule.title")}</label>
        <div className="flex items-center gap-2">
          {templates && (
            <Select value="" onValueChange={applyTpl}>
              <SelectTrigger data-testid={`${testid}-tpl-select`} className="h-7 w-40 rounded-sm border-zinc-300 text-xs"><SelectValue placeholder={t("rule.loadTemplate")} /></SelectTrigger>
              <SelectContent>
                {tpls.length === 0 && <div className="px-2 py-1.5 text-xs text-zinc-400">{t("rule.noTemplates")}</div>}
                {tpls.map((tp) => (
                  <div key={tp.id} className="flex items-center">
                    <SelectItem value={tp.id} className="flex-1 text-xs">{tp.name}</SelectItem>
                    <button onClick={(e) => delTpl(tp.id, e)} data-testid={`${testid}-tpl-del-${tp.id}`} className="px-1.5 text-zinc-300 hover:text-red-600"><Trash2 size={12} /></button>
                  </div>
                ))}
              </SelectContent>
            </Select>
          )}
          {clauses.length > 1 && (
            <Select value={join} onValueChange={(j) => setBoth(clauses, j)}>
              <SelectTrigger data-testid={`${testid}-join`} className="h-7 w-36 rounded-sm border-zinc-300 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{joinOpts.map((j) => <SelectItem key={j} value={j} className="text-xs">{joinLabel(j)}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {clauses.map((c, i) => {
          const isBool = !isExo && BOOL_PROPS.includes(c.property);
          const isList = !isExo && LIST_OPS.includes(c.operator);
          return (
            <div key={i} className="flex items-center gap-2" data-testid={`${testid}-row-${i}`}>
              <Select value={c.property} onValueChange={(v) => update(i, "property", v)}>
                <SelectTrigger data-testid={`${testid}-prop-${i}`} className="h-9 flex-1 rounded-sm border-zinc-300 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{schema.properties.map((p) => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={c.operator} onValueChange={(v) => update(i, "operator", v)}>
                <SelectTrigger data-testid={`${testid}-op-${i}`} className="h-9 w-28 rounded-sm border-zinc-300 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{schema.operators.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
              </Select>
              {isBool ? (
                <Select value={String(c.value || "true")} onValueChange={(v) => update(i, "value", v)}>
                  <SelectTrigger data-testid={`${testid}-val-${i}`} className="h-9 flex-1 rounded-sm border-zinc-300 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="true">true</SelectItem><SelectItem value="false">false</SelectItem></SelectContent>
                </Select>
              ) : (
                <Input value={c.value || ""} onChange={(e) => update(i, "value", e.target.value)} data-testid={`${testid}-val-${i}`}
                  placeholder={isList ? t("rule.valListPh") : t("rule.valPh")} className="h-9 flex-1 rounded-sm border-zinc-300 text-xs" />
              )}
              <button type="button" onClick={() => remove(i)} disabled={clauses.length === 1} data-testid={`${testid}-remove-${i}`}
                className="flex h-9 w-9 shrink-0 items-center justify-center border border-zinc-200 text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"><X size={14} /></button>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={add} data-testid={`${testid}-add`}
          className="inline-flex items-center gap-1.5 border border-dashed border-zinc-300 px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:border-primary hover:text-primary">
          <Plus size={13} /> {t("rule.addClause")}
        </button>
        {templates && !showSave && (
          <button type="button" onClick={() => setShowSave(true)} data-testid={`${testid}-save-tpl`}
            className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-500 hover:text-primary">
            <BookmarkPlus size={13} /> {t("rule.saveTemplate")}
          </button>
        )}
        {preview && (
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-primary" data-testid={`${testid}-count`}>
            <Users2 size={13} />{counting ? <Loader2 size={12} className="animate-spin" /> : count === null ? "—" : t("rule.previewCount").replace("{n}", count)}
          </span>
        )}
      </div>

      {templates && showSave && (
        <div className="flex items-center gap-2">
          <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder={t("rule.templateName")} data-testid={`${testid}-tpl-name`} className="h-8 rounded-sm border-zinc-300 text-xs" />
          <Button type="button" size="sm" onClick={saveTpl} disabled={savingTpl} data-testid={`${testid}-tpl-confirm`} className="h-8 rounded-sm bg-primary text-xs hover:bg-[#002480]"><Save size={12} className="mr-1" /> {t("common.save")}</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setShowSave(false)} className="h-8 rounded-sm text-xs">{t("common.cancel")}</Button>
        </div>
      )}

      <div className="border border-zinc-200 bg-white px-3 py-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t("rule.preview")}</div>
        <code data-testid={`${testid}-preview`} className="block break-all font-mono text-[11px] text-zinc-800">{previewText || t("rule.previewEmpty")}</code>
      </div>
    </div>
  );
}
